import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddHearsayDialog } from '../../hearsay/AddHearsayDialog';
import { useIntelClaims } from '../../hearsay/hooks';
import { intelClaimsSection } from '../../hearsay/IntelClaimsRailSection';
import { deliveryLogTouchpointHref, growthLogTouchpointHref } from '../contact/logTouchpointLinks';
export function useGovOrgIntel(id, name) {
    const query = useIntelClaims({
        kind: 'GOVERNMENT_ORG',
        id
    });
    const [open, setOpen] = useState(false);
    const canCreate = query.data?.canCreate ?? false;
    const section = intelClaimsSection(query.data?.claims ?? [], {
        canCreate: false,
        onAdd: ()=>setOpen(true)
    });
    if (query.isError) section.children = /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "ghost",
        onClick: ()=>void query.refetch(),
        children: "Retry loading intel"
    });
    else if (query.isPending) section.children = 'Loading intel…';
    return {
        section,
        action: canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
            variant: "secondary",
            onClick: ()=>setOpen(true),
            children: "Add hearsay"
        }) : null,
        dialog: /*#__PURE__*/ _jsx(AddHearsayDialog, {
            open: open,
            onOpenChange: setOpen,
            defaultLinks: [
                {
                    linkRole: 'ABOUT',
                    targetKind: 'GOVERNMENT_ORG',
                    targetId: id,
                    label: name,
                    locked: true
                }
            ],
            canCreateKinds: query.data?.canCreateKinds
        })
    };
}
export function GovOrgLogTouchpoint({ dossier, people }) {
    const [open, setOpen] = useState(false);
    const [target, setTarget] = useState();
    const [person, setPerson] = useState();
    const navigate = useNavigate();
    const refs = dossier.appearances.flatMap((c)=>c.primary ? [
            c.primary,
            ...c.contexts
        ] : c.contexts).filter((r)=>r.kind === 'PURSUIT' || r.kind === 'PROGRAM');
    const targets = [
        ...new Map(refs.map((r)=>[
                `${r.kind}:${r.id}`,
                r
            ])).values()
    ];
    const chosen = targets.find((r)=>`${r.kind}:${r.id}` === target);
    if (!targets.length) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: ()=>setOpen(true),
                children: "Log touchpoint"
            }),
            /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: open,
                onOpenChange: setOpen,
                title: "Log touchpoint",
                description: "Pick the pursuit or program it was about. You'll finish it there.",
                size: "md",
                children: [
                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Pursuit or program",
                        value: target,
                        onValueChange: setTarget,
                        options: targets.map((r)=>({
                                value: `${r.kind}:${r.id}`,
                                label: r.name
                            }))
                    }),
                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Person (optional)",
                        value: person,
                        onValueChange: setPerson,
                        options: [
                            {
                                value: '',
                                label: 'No person selected'
                            },
                            ...people.map((p)=>({
                                    value: p.contactId,
                                    label: p.name
                                }))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                        confirmLabel: "Continue",
                        confirmDisabled: !chosen,
                        onCancel: ()=>setOpen(false),
                        onConfirm: ()=>{
                            if (!chosen) return;
                            setOpen(false);
                            const href = chosen.kind === 'PURSUIT' ? growthLogTouchpointHref(chosen.id, person ?? '') : deliveryLogTouchpointHref(chosen.id, person ?? '');
                            navigate(person ? href : href.replace(/&contact=$/, ''));
                        }
                    })
                ]
            })
        ]
    });
}
